import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import {
  fetchAdminPlans,
  updatePlanAdmin,
  grantCreditsAdmin,
  fetchAdminPaymentReports,
  approvePaymentReportAdmin,
  rejectPaymentReportAdmin,
  type PlanItem,
  type PaymentReportItem,
} from '@/services/billingApi'
import { traceService } from '../services/traceService'

const AdminPlansPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'plans' | 'reports'>('plans')

  // Estado para edición de planes
  const [editingPlan, setEditingPlan] = useState<PlanItem | null>(null)
  const [editPriceUsd, setEditPriceUsd] = useState<number>(0)
  const [editCredits, setEditCredits] = useState<number>(0)
  const [editTrialDays, setEditTrialDays] = useState<number>(0)
  const [editName, setEditName] = useState<string>('')
  const [editIsActive, setEditIsActive] = useState<boolean>(true)

  // Estado para otorgar créditos
  const [grantTenantId, setGrantTenantId] = useState('')
  const [grantAmount, setGrantAmount] = useState<number>(500)
  const [grantReason, setGrantReason] = useState('Bonificación de soporte')
  const [grantFeedback, setGrantFeedback] = useState<string | null>(null)

  // Estado para validación de reportes de pago
  const [reportFilter, setReportFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')
  const [selectedReceiptUrl, setSelectedReceiptUrl] = useState<string | null>(null)
  const [rejectingReportId, setRejectingReportId] = useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')

  const { data: plans = [], isLoading: loadingPlans } = useQuery({
    queryKey: ['adminPlans'],
    queryFn: fetchAdminPlans,
  })

  const { data: reports = [], isLoading: loadingReports } = useQuery({
    queryKey: ['adminPaymentReports', reportFilter],
    queryFn: () => fetchAdminPaymentReports(reportFilter),
    refetchInterval: 15000,
  })

  const { data: tenants = [] } = useQuery({
    queryKey: ['adminTenantsList'],
    queryFn: async () => {
      const res = await traceService.getTenants()
      return res.data || []
    },
  })

  const pendingCount = reports.filter((r) => r.status === 'pending').length

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<PlanItem> }) =>
      updatePlanAdmin(id, updates),
    onSuccess: () => {
      toast.success('Plan actualizado exitosamente')
      queryClient.invalidateQueries({ queryKey: ['adminPlans'] })
      queryClient.invalidateQueries({ queryKey: ['availablePlans'] })
      setEditingPlan(null)
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Error al actualizar el plan')
    },
  })

  const grantMutation = useMutation({
    mutationFn: () => grantCreditsAdmin(grantTenantId, grantAmount, grantReason),
    onSuccess: () => {
      toast.success(`Se otorgaron ${grantAmount} créditos exitosamente`)
      setGrantFeedback(`✅ Se otorgaron ${grantAmount} créditos exitosamente al tenant ${grantTenantId}`)
      setGrantTenantId('')
      setGrantAmount(500)
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || err.message)
      setGrantFeedback(`❌ Error: ${err?.response?.data?.error || err.message}`)
    },
  })

  const approveMutation = useMutation({
    mutationFn: approvePaymentReportAdmin,
    onSuccess: (data) => {
      toast.success(data.message || 'Pago aprobado y plan activado exitosamente')
      queryClient.invalidateQueries({ queryKey: ['adminPaymentReports'] })
      queryClient.invalidateQueries({ queryKey: ['adminPlans'] })
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Error al aprobar el pago')
    },
  })

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rejectPaymentReportAdmin(id, reason),
    onSuccess: () => {
      toast.success('Reporte de pago rechazado exitosamente')
      queryClient.invalidateQueries({ queryKey: ['adminPaymentReports'] })
      setRejectingReportId(null)
      setRejectionReason('')
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error || 'Error al rechazar el pago')
    },
  })

  const startEditing = (p: PlanItem) => {
    setEditingPlan(p)
    setEditName(p.name)
    setEditPriceUsd(p.priceCents / 100)
    setEditCredits(p.creditsIncluded)
    setEditTrialDays(p.trialDays)
    setEditIsActive(p.isActive)
  }

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingPlan) return

    updateMutation.mutate({
      id: editingPlan.id,
      updates: {
        name: editName,
        priceCents: Math.round(editPriceUsd * 100),
        creditsIncluded: Number(editCredits),
        monthlyMsgLimit: Number(editCredits),
        trialDays: Number(editTrialDays),
        isActive: editIsActive,
      },
    })
  }

  const handleApprove = (report: PaymentReportItem) => {
    if (confirm(`¿Confirmas que recibiste el Pago Móvil de ${report.referenceNumber} por Bs. ${report.amountBs.toLocaleString()} para activar el plan ${report.plan?.name}?`)) {
      approveMutation.mutate(report.id)
    }
  }

  const handleRejectSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!rejectingReportId || !rejectionReason.trim()) return
    rejectMutation.mutate({ id: rejectingReportId, reason: rejectionReason.trim() })
  }

  return (
    <div className="admin-page-container p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900 tracking-tight">Gestión de Planes y Pagos</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Administra tarifas, créditos por suscripción y valida los reportes de Pago Móvil de los clientes.
          </p>
        </div>

        {/* Selector de Pestañas */}
        <div className="flex bg-gray-100 p-1 rounded-xl text-sm font-semibold">
          <button
            onClick={() => setActiveTab('plans')}
            className={`px-4 py-2 rounded-lg transition-all ${
              activeTab === 'plans'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            Planes y Precios
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
              activeTab === 'reports'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            Reportes de Pago Móvil
            {pendingCount > 0 && (
              <span className="bg-amber-500 text-white text-[10px] font-black px-2 py-0.5 rounded-full">
                {pendingCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          PESTAÑA 1: GESTIÓN DE PLANES Y PRECIOS
          ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'plans' && (
        <div className="space-y-6">
          {/* TABLA DE PLANES */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-base font-bold text-gray-800">Planes Configurados en el Sistema</h2>
              {loadingPlans && <span className="text-xs text-gray-400">Actualizando...</span>}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gray-50 text-xs uppercase font-bold text-gray-500 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3">Código</th>
                    <th className="px-6 py-3">Nombre</th>
                    <th className="px-6 py-3">Precio USD</th>
                    <th className="px-6 py-3">Créditos IA</th>
                    <th className="px-6 py-3">Días Prueba</th>
                    <th className="px-6 py-3">Default</th>
                    <th className="px-6 py-3">Estado</th>
                    <th className="px-6 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {plans.map((p: PlanItem) => (
                    <tr key={p.id} className="hover:bg-gray-50/70 transition-colors">
                      <td className="px-6 py-4 font-mono font-bold text-gray-700">{p.code}</td>
                      <td className="px-6 py-4 font-semibold text-gray-900">{p.name}</td>
                      <td className="px-6 py-4 font-bold text-gray-800">
                        {p.priceCents === 0 ? 'Gratis' : `$${(p.priceCents / 100).toFixed(2)}`}
                      </td>
                      <td className="px-6 py-4 font-bold text-emerald-700">
                        {p.creditsIncluded.toLocaleString()} créditos
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {p.trialDays > 0 ? `${p.trialDays} días` : 'Sin trial'}
                      </td>
                      <td className="px-6 py-4">
                        {p.isDefault ? (
                          <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                            Por Defecto
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                            p.isActive
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {p.isActive ? 'Activo' : 'Oculto'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => startEditing(p)}
                          className="px-3 py-1.5 text-xs font-bold bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                        >
                          Modificar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* FORMULARIO DE RECARGA MANUAL DE CRÉDITOS */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-base font-bold text-gray-800 mb-1">Recargar Créditos Manualmente a un Tenant</h2>
            <p className="text-xs text-gray-500 mb-4">
              Otorga créditos adicionales a un comercio específico por motivo de soporte o bonificación.
            </p>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                if (!grantTenantId.trim()) {
                  toast.error('Selecciona o ingresa el ID del tenant')
                  return
                }
                grantMutation.mutate()
              }}
              className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end"
            >
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Tenant Destino</label>
                {tenants.length > 0 ? (
                  <select
                    value={grantTenantId}
                    onChange={(e) => setGrantTenantId(e.target.value)}
                    required
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none bg-white"
                  >
                    <option value="">-- Seleccionar Tenant --</option>
                    {tenants.map((t: any) => (
                      <option key={t.id} value={t.id}>
                        {t.name || t.slug || t.id} ({t.id.slice(0, 8)}...)
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    required
                    placeholder="ej. cmtpaxjcl..."
                    value={grantTenantId}
                    onChange={(e) => setGrantTenantId(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none"
                  />
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Cantidad de Créditos</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={grantAmount}
                  onChange={(e) => setGrantAmount(Number(e.target.value))}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Motivo / Justificación</label>
                <input
                  type="text"
                  value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none"
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={grantMutation.isPending}
                  className="w-full py-2.5 px-4 bg-[#075f55] hover:bg-[#064e46] text-white text-xs font-bold rounded-xl shadow-sm transition-colors"
                >
                  {grantMutation.isPending ? 'Otorgando...' : 'Otorgar Créditos'}
                </button>
              </div>
            </form>

            {grantFeedback && (
              <p className="mt-3 text-xs font-semibold text-gray-700 bg-gray-50 p-2.5 rounded-lg border border-gray-200">
                {grantFeedback}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          PESTAÑA 2: VALIDACIÓN DE REPORTES DE PAGO MÓVIL
          ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'reports' && (
        <div className="space-y-6">
          {/* Filtros de estado */}
          <div className="flex gap-2 text-xs font-bold">
            {(['all', 'pending', 'approved', 'rejected'] as const).map((st) => (
              <button
                key={st}
                onClick={() => setReportFilter(st)}
                className={`px-3 py-1.5 rounded-lg transition-colors border ${
                  reportFilter === st
                    ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {{
                  all: 'Todos los Reportes',
                  pending: `Pendientes (${pendingCount})`,
                  approved: 'Aprobados',
                  rejected: 'Rechazados',
                }[st]}
              </button>
            ))}
          </div>

          {/* Tabla de Reportes */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-base font-bold text-gray-800">Comprobantes de Pago Reportados</h2>
              {loadingReports && <span className="text-xs text-gray-400">Actualizando...</span>}
            </div>

            {reports.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                No hay reportes de pago móvil registrados con este filtro.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 text-xs uppercase font-bold text-gray-500 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3">Fecha</th>
                      <th className="px-4 py-3">Comercio / Tenant</th>
                      <th className="px-4 py-3">Plan Solicitado</th>
                      <th className="px-4 py-3">Referencia</th>
                      <th className="px-4 py-3">Monto Bs. (Tasa BCV)</th>
                      <th className="px-4 py-3">Comprobante</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3 text-right">Acción Admin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reports.map((r: PaymentReportItem) => {
                      const isPending = r.status === 'pending'
                      return (
                        <tr key={r.id} className="hover:bg-gray-50/70 transition-colors">
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                            {new Date(r.createdAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-3">
                            <strong className="text-gray-900 block">{r.tenant?.name || r.tenantId}</strong>
                            <span className="text-[10px] text-gray-400 font-mono">{r.tenantId}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-bold text-gray-800">{r.plan?.name}</span>
                            <span className="text-[11px] text-emerald-700 block font-semibold">
                              +{r.plan?.creditsIncluded.toLocaleString()} créditos
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono font-bold text-gray-900">
                            {r.referenceNumber}
                            {r.issuingBank && (
                              <span className="text-[10px] text-gray-400 block font-sans">{r.issuingBank}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <strong className="text-gray-900">
                              Bs. {r.amountBs.toLocaleString('es-VE', { minimumFractionDigits: 2 })}
                            </strong>
                            <span className="text-[10px] text-gray-500 block">
                              ${r.amountUsd} USD · Tasa: {r.exchangeRate}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setSelectedReceiptUrl(r.receiptUrl)}
                              className="text-xs text-[#075f55] hover:underline font-bold flex items-center gap-1"
                            >
                              Ver capture
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                                r.status === 'approved'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : r.status === 'rejected'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {r.status === 'approved'
                                ? 'Aprobado'
                                : r.status === 'rejected'
                                ? 'Rechazado'
                                : 'Pendiente'}
                            </span>
                            {r.status === 'rejected' && r.rejectionReason && (
                              <span className="text-[10px] text-red-600 block mt-0.5">
                                {r.rejectionReason}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {isPending ? (
                              <div className="flex justify-end gap-1.5">
                                <button
                                  onClick={() => handleApprove(r)}
                                  disabled={approveMutation.isPending}
                                  className="px-3 py-1.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors shadow-sm"
                                >
                                  Aprobar
                                </button>
                                <button
                                  onClick={() => {
                                    setRejectingReportId(r.id)
                                    setRejectionReason('')
                                  }}
                                  className="px-2.5 py-1.5 text-xs font-bold bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors border border-red-200"
                                >
                                  Rechazar
                                </button>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 italic">Validado</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL: VER COMPROBANTE EN TAMAÑO COMPLETO ── */}
      {selectedReceiptUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-4 overflow-hidden relative shadow-2xl">
            <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
              <h3 className="font-bold text-sm text-gray-800">Comprobante de Pago Móvil</h3>
              <button
                onClick={() => setSelectedReceiptUrl(null)}
                className="text-gray-400 hover:text-gray-700 text-sm font-bold px-2 py-1"
              >
                ✕ Cerrar
              </button>
            </div>
            <div className="flex justify-center bg-gray-50 rounded-xl p-2 max-h-[75vh] overflow-auto">
              <img
                src={selectedReceiptUrl}
                alt="Capture de pago móvil"
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: RECHAZAR REPORTE DE PAGO ── */}
      {rejectingReportId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200">
            <h3 className="font-bold text-base text-gray-900 mb-2">Rechazar Reporte de Pago</h3>
            <p className="text-xs text-gray-500 mb-4">
              Indica la razón por la cual no se valida este pago para que el cliente sea informado.
            </p>
            <form onSubmit={handleRejectSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Motivo del rechazo</label>
                <textarea
                  required
                  rows={3}
                  placeholder="Ej: La referencia no coincide con los movimientos del banco o el monto está incompleto..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRejectingReportId(null)}
                  className="px-4 py-2 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={rejectMutation.isPending}
                  className="px-4 py-2 text-xs font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl shadow-sm"
                >
                  {rejectMutation.isPending ? 'Rechazando...' : 'Confirmar Rechazo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── MODAL DE EDICIÓN DE PLAN ── */}
      {editingPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 border border-gray-200">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Editar Plan: <span className="text-[#075f55]">{editingPlan.name}</span>
            </h3>

            <form onSubmit={handleSaveEdit} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">Nombre Comercial</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Precio en USD ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editPriceUsd}
                  onChange={(e) => setEditPriceUsd(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Créditos de IA Incluidos</label>
                <input
                  type="number"
                  min="0"
                  value={editCredits}
                  onChange={(e) => setEditCredits(parseInt(e.target.value, 10) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Días de Prueba (0 para deshabilitar)</label>
                <input
                  type="number"
                  min="0"
                  value={editTrialDays}
                  onChange={(e) => setEditTrialDays(parseInt(e.target.value, 10) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#075f55] outline-none"
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={editIsActive}
                  onChange={(e) => setEditIsActive(e.target.checked)}
                  className="rounded text-[#075f55] focus:ring-[#075f55]"
                />
                <label htmlFor="isActive" className="font-semibold text-gray-700">
                  Plan Activo y Visible para Clientes
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setEditingPlan(null)}
                  className="px-4 py-2 font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 font-bold text-white bg-[#075f55] hover:bg-[#064e46] rounded-xl shadow-sm"
                >
                  {updateMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPlansPage
